import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/ppcp";
import { track } from "@/lib/analytics";

/** Progress lives in one place per program, so the Library, Program Home, Day Index and Day Page
 *  always show the same numbers without a page refresh. Writes are optimistic: the UI updates
 *  immediately and the server's derived summary replaces it when the save lands. */
const ProgressContext = createContext(null);

/** Finishing a plan is reported once per plan, not once per crossing of the finish line — so
 *  unticking and re-ticking the last meal can't inflate the count. Cleared when progress resets. */
const COMPLETED_KEY = (slug) => `ppcp_plan_completed:${slug}`;

const alreadyCompleted = (slug) => {
  try {
    return window.localStorage.getItem(COMPLETED_KEY(slug)) === "1";
  } catch {
    return false;
  }
};

const markCompleted = (slug, done) => {
  try {
    if (done) window.localStorage.setItem(COMPLETED_KEY(slug), "1");
    else window.localStorage.removeItem(COMPLETED_KEY(slug));
  } catch {
    /* private mode: the event may repeat, the plan still works */
  }
};

const recompute = (summary, meals) => {
  const total = summary.total_days;
  const completedDays = [];
  for (let n = 1; n <= total; n += 1) {
    const required = summary.required_slots?.[n];
    const ticked = meals[n] ?? [];
    if (required?.length && required.every((slot) => ticked.includes(slot))) completedDays.push(n);
  }
  const remaining = [];
  for (let n = 1; n <= total; n += 1) if (!completedDays.includes(n)) remaining.push(n);

  return {
    ...summary,
    meals,
    completed_days: completedDays,
    days_complete: completedDays.length,
    percent: total ? Math.round((completedDays.length / total) * 100) : 0,
    resume_day: remaining.length ? remaining[0] : total,
    started: Object.values(meals).some((slots) => slots.length),
    finished: !remaining.length,
  };
};

export const ProgressProvider = ({ slug, children }) => {
  const [summary, setSummary] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/programs/${slug}/progress`);
      setSummary(data);
      return data;
    } catch {
      return null;
    }
  }, [slug]);

  useEffect(() => {
    setSummary(null);
    refresh();
  }, [refresh]);

  /** Tick or untick one meal. Saves itself — there is no Save button anywhere. */
  const setMeal = useCallback(
    async (dayNumber, slot, completed, requiredSlots) => {
      const previous = summary;
      if (previous) {
        const ticked = previous.meals?.[dayNumber] ?? [];
        const meals = {
          ...previous.meals,
          [dayNumber]: completed ? [...new Set([...ticked, slot])] : ticked.filter((s) => s !== slot),
        };
        setSummary(
          recompute(
            {
              ...previous,
              required_slots: { ...previous.required_slots, [dayNumber]: requiredSlots },
            },
            meals
          )
        );
      }

      try {
        const { data } = await api.put(
          `/programs/${slug}/progress/days/${dayNumber}/meals/${slot}`,
          { completed }
        );
        setSummary((current) => ({ ...data, required_slots: current?.required_slots }));
        // Only a tick is an event; unticking a meal is a correction, not an achievement.
        if (completed) {
          track("meal_completed", { meal_type: slot, day_number: dayNumber, plan_slug: slug });
        }
        // The moment the last required meal of the last day lands — once per plan.
        if (completed && data.finished && !alreadyCompleted(slug)) {
          markCompleted(slug, true);
          track("plan_completed", { plan_slug: slug, total_days: data.total_days });
        }
      } catch {
        setSummary(previous);
        toast.error("We couldn't save that just now. Please try again.");
      }
    },
    [slug, summary]
  );

  const reset = useCallback(async () => {
    const { data } = await api.delete(`/programs/${slug}/progress`);
    // Starting the plan again means finishing it again is a new achievement.
    markCompleted(slug, false);
    setSummary(data);
  }, [slug]);

  const value = useMemo(() => ({ summary, refresh, setMeal, reset }), [summary, refresh, setMeal, reset]);
  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
};

export const useProgress = () => useContext(ProgressContext);
